import React, { useCallback, useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OwnerType, TOwnerLink, TOwnershipStructure } from '../types';
import { OwnerFormModal } from './OwnerForms/OwnerFormModal';
import { Box, Divider, Typography } from '@mui/material';
import { DeleteModal } from '~/components/modals/DeleteModal/DeleteModal';
import { OwnerCardList } from './OwnerCard/OwnerCardList';
import { FormCheckbox } from '~/components/FormComponents/FormCheckbox';
import { FormInput } from '~/components/FormComponents/FormInput';
import { ROOT_OWNER_NODE } from '../constants';
import OwnershipStructureDiagram from '~/modules/identification/forms/OwnershipStructure/components/Diagram/OwnershipStructureDiagram.tsx';
import { BeneficiaryOwnerList } from './BeneficiaryOwnersList';
import { InfoTooltip } from '~/components/InfoTooltip/InfoTooltip';
import { commonStyles } from '~/theme/styles/commonStyles';
import { styles } from '../styles';
import { roundNumber } from '~/helpers/utils';

export const OwnershipStructure = () => {
  const { control, getValues, setValue, trigger, clearErrors } =
    useFormContext<TOwnershipStructure>();
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [deleteIndex, setDeleteIndex] = useState<number>(-1);
  const [deleteFunc, setDeleteFunc] = useState<(() => void) | undefined>();

  const [isFoundersAndBeneficiariesAbsent, ownershipStructureSpecifics] = useWatch({
    name: ['isFoundersAndBeneficiariesAbsent', 'ownershipStructureSpecifics', 'link'],
    control,
  });

  const showDeleteModal = deleteIndex >= 0 || deleteIndex === -2;
  const confirmBtnText = deleteIndex === -2 ? 'Очистити' : 'Видалити';

  const handleOpen = useCallback(
    (index: number) => {
      setSelectedIndex(index);
    },
    [setSelectedIndex]
  );

  const handleDeleteOpen = useCallback(
    (index: number) => {
      setDeleteIndex(index);
    },
    [setDeleteIndex]
  );

  const handleCalculateOwnershipPercentage = () => {
    const links = getValues('link');
    if (links?.length > 0) {
      const total = links.reduce(
        (sum: number, link: TOwnerLink) =>
          link.toNodeId === ROOT_OWNER_NODE ? sum + (Number(link.ownershipPercentage) || 0) : sum,
        0
      );
      const roundedTotal = roundNumber(total);

      setValue('ownershipPercentageTotal', roundedTotal, {
        shouldValidate: true,
        shouldTouch: true,
      });
    } else {
      setValue('ownershipPercentageTotal', 0, {
        shouldValidate: true,
        shouldTouch: true,
      });
    }
  };

  useEffect(() => {
    handleCalculateOwnershipPercentage();
  }, []);

  const handleClose = () => {
    setSelectedIndex(-1);
  };

  const handleDeleteClose = () => {
    setDeleteIndex(-1);
  };

  const handleDeleteItem = useCallback(
    (linkIndex: number) => {
      const links = getValues('link');
      const owners = getValues('owners');
      const targetLink = links[linkIndex];
      if (!targetLink) return;

      const ownerId = targetLink.fromNodeId;
      const parentOwnerId = targetLink.toNodeId;
      const parentOwnerIndex = owners.findIndex((owner) => owner.id === parentOwnerId);
      const ownerIndex = owners.findIndex((owner) => owner.id === ownerId);

      const rootsToDelete = links.filter(
        (link) =>
          link.id === targetLink.id ||
          (link.fromNodeId === ownerId && link.toNodeId === parentOwnerId)
      );

      const linkIdsToRemove = new Set<string>(rootsToDelete.map((l) => l.id));
      let grew = true;
      while (grew) {
        grew = false;
        for (const link of links) {
          if (
            link.parentLinkId &&
            linkIdsToRemove.has(link.parentLinkId) &&
            !linkIdsToRemove.has(link.id)
          ) {
            linkIdsToRemove.add(link.id);
            grew = true;
          }
        }
      }

      const remainingLinks = links
        .filter((link) => !linkIdsToRemove.has(link.id))
        .map((link) => ({ ...link }));

      const affectedParents = new Set(
        links
          .filter((l) => linkIdsToRemove.has(l.id))
          .map((l) => l.parentLinkId)
          .filter((pid) => pid === null || !linkIdsToRemove.has(pid))
      );
      affectedParents.forEach((parentId) => {
        remainingLinks
          .filter((l) => l.parentLinkId === parentId)
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .forEach((l, i) => {
            l.displayOrder = i;
          });
      });

      const referencedOwnerIds = new Set(remainingLinks.flatMap((l) => [l.fromNodeId, l.toNodeId]));
      const remainingOwners = owners.filter((owner) => referencedOwnerIds.has(owner.id));

      setValue('link', remainingLinks, { shouldDirty: true });
      if (remainingOwners.length !== owners.length) {
        setValue('owners', remainingOwners, { shouldDirty: true });
      }

      setDeleteIndex(-1);
      trigger('isFoundersAndBeneficiariesAbsent');
      trigger('ownershipPercentageTotal');
      clearErrors(`link.${linkIndex}`);
      clearErrors(`owners.${ownerIndex}`);

      if (parentOwnerIndex !== -1) {
        trigger(`owners.${parentOwnerIndex}`);
      } else {
        handleCalculateOwnershipPercentage();
      }
    },
    [getValues, setValue, trigger, handleCalculateOwnershipPercentage, setDeleteIndex]
  );

  const handleClearOwners = () => {
    deleteFunc?.();
    setDeleteIndex(-1);
    setDeleteFunc(undefined);
  };

  const getDeleteTitle = (deleteIndex: number) => {
    if (deleteIndex === -1) return '';
    if (deleteIndex === -2) {
      return 'Якщо ви активуєте цю опцію, всі введені дані будуть очищені. Продовжити?';
    } else {
      const link = getValues(`link.${deleteIndex}`);
      const links = getValues('link');
      const { firstName, lastName, middleName, fullName, id, type } =
        getValues('owners').find((owner) => owner.id === link.fromNodeId) || {};
      const ownerName = [lastName, firstName, middleName].join(' ').trim() || fullName || '';

      const hasChildrens = links.some((link) => link.toNodeId === id);

      const ownerText = [OwnerType.FREE_FLOAT, OwnerType.SHAREHOLDER_GROUP].includes(
        type as OwnerType
      )
        ? `картку «${ownerName}»`
        : `власника «${ownerName}»`;

      return hasChildrens
        ? `Видаляючи цього ${ownerText}, ви також видалите всіх його власників наступних рівнів. Продовжити?`
        : `Ви впевнені, що бажаєте видалити ${ownerText}?`;
    }
  };

  const handleCheckboxChange = (isChecked: boolean) => {
    const owners = getValues(`owners`);

    if (isChecked && owners.length) {
      setDeleteIndex(-2);
      setDeleteFunc(() => () => {
        setValue('owners', []);
        setValue('link', []);
        setValue('isFoundersAndBeneficiariesAbsent', true, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue('ownershipPercentageTotal', null);
        setDeleteIndex(-1);
      });
      return;
    }
    setValue('isFoundersAndBeneficiariesAbsent', isChecked, {
      shouldValidate: true,
      shouldDirty: true,
    });
    setValue('ownershipStructureSpecifics', '', { shouldDirty: true });
  };

  return (
    <Box>
      <Box sx={{ width: 250 }}>
        <FormCheckbox
          name="isFoundersAndBeneficiariesAbsent"
          label={
            <Typography variant="body2" sx={styles.isAbsendTitle}>
              Відсутні засновники/учасники
              <InfoTooltip
                placement="bottom"
                title={
                  "Ця опція призначена для організацій, у яких відсутні власники або засновники у традиційному розумінні (наприклад, благодійні організації, партії, кооперативи). Вкажіть будь-яку інформацію, що може допомогти банку зрозуміти специфіку вашої організації. Заповнення не є обов'язковим."
                }
              />
            </Typography>
          }
          disableFieldOnChange
          onChange={handleCheckboxChange}
          fullWidth
        />
      </Box>

      {isFoundersAndBeneficiariesAbsent ? (
        <Box sx={{ position: 'relative' }}>
          <FormInput
            name="ownershipStructureSpecifics"
            slotProps={{ htmlInput: { maxLength: 500 } }}
            placeholder="Вкажіть інформацію, що може охарактеризувати специфіку структури власності"
            multiline
            rows={4}
            fullWidth
          />
          <Typography
            sx={{ color: 'textSecondary', bottom: 14, right: 14, position: 'absolute' }}
            variant="body2"
          >
            {ownershipStructureSpecifics?.length} / 500
          </Typography>
        </Box>
      ) : (
        <Box sx={commonStyles.columnFlexContainer}>
          <OwnerCardList
            handleDeleteOpen={handleDeleteOpen}
            handleOpen={handleOpen}
            setDeleteFunc={setDeleteFunc}
            handleCalculateOwnershipPercentage={handleCalculateOwnershipPercentage}
          />
          <Box sx={{ width: '50%' }}>
            <FormInput
              name="ownershipPercentageTotal"
              disabled
              placeholder="Введіть частку в статутному капіталі"
              label="Загальна частка всіх учасників у статутному капіталі (%)"
              fullWidth
              slotProps={{
                htmlInput: { 'data-testid': 'ownershipPercentageTotal' },
              }}
            />
          </Box>
          <OwnershipStructureDiagram />
          <Divider sx={{ mt: 2, mb: 2 }} />
          <BeneficiaryOwnerList />
        </Box>
      )}

      <OwnerFormModal
        index={selectedIndex}
        onClose={handleClose}
        handleCalculateOwnershipPercentage={handleCalculateOwnershipPercentage}
      />
      <DeleteModal
        closeDeleteModal={handleDeleteClose}
        confirmDelete={() =>
          deleteIndex !== -2 ? handleDeleteItem(deleteIndex) : handleClearOwners()
        }
        showDeleteModal={showDeleteModal}
        title={getDeleteTitle(deleteIndex)}
        hideIcon
        cancelBtnText="Скасувати"
        confirmBtnText={confirmBtnText}
      />
    </Box>
  );
};
